import type { AuthAdapter, AuthUser, SignUpPayload } from '@vextura/shell';
import { getConsoleConfig } from './loadConfig.js';

/**
 * HTTP AuthAdapter pointed at vex-auth. Uses the OAuth2 client_credentials
 * flow exposed by vex-auth:
 *
 *   POST /auth/token   {grant_type:"client_credentials", client_id, client_secret}
 *                      → { access_token, expires_in }
 *
 * vex-auth has no JSON whoami/logout endpoint — the access_token is a
 * JWT, so we decode it locally to recover the user profile and treat
 * sessions as in-memory only. (When @vextura/api-clients-auth ships we
 * regenerate this adapter and gain refresh/revocation.)
 */

interface JwtClaims {
  sub?: string;
  email?: string;
  display_name?: string;
  name?: string;
  preferred_username?: string;
  tenant?: string;
  tenant_id?: string;
  // Multi-region P1: vex-auth stamps `region`; some legacy issuers use `cluster`.
  region?: string;
  cluster?: string;
  roles?: ReadonlyArray<string>;
  exp?: number; // unix seconds
  iat?: number; // unix seconds
}

function decodeJwtClaims(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;
  try {
    // base64url → base64 → JSON
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
    return JSON.parse(atob(b64)) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * True if the JWT's `exp` claim is in the past (with 5s safety margin
 * to avoid a race between the check and the API call using the token).
 */
export function isJwtExpired(token: string): boolean {
  const c = decodeJwtClaims(token);
  if (!c || typeof c.exp !== 'number') return false;
  const now = Math.floor(Date.now() / 1000);
  return c.exp <= now + 5;
}

/**
 * Milliseconds until the JWT expires. Returns 0 if already expired or missing exp.
 * Used by AuthProvider to schedule an auto-signOut before the token dies mid-call.
 */
export function jwtExpiresInMs(token: string): number {
  const c = decodeJwtClaims(token);
  if (!c || typeof c.exp !== 'number') return 0;
  const ms = c.exp * 1000 - Date.now();
  return ms > 0 ? ms : 0;
}

function userFromClaims(token: string, fallbackUsername?: string): AuthUser {
  const c = decodeJwtClaims(token) ?? {};
  return {
    id: c.sub ?? fallbackUsername ?? 'unknown',
    name: c.display_name ?? c.name ?? c.preferred_username ?? fallbackUsername ?? c.sub ?? 'unknown',
    email: c.email ?? '',
    homeTenant: c.tenant ?? c.tenant_id ?? '',
    // Multi-region hardening P1: vex-auth stamps `region` into every JWT.
    // Fall back to legacy `cluster` claim (pre-P1 vex-id tokens) so migration
    // is non-disruptive. Empty for tokens minted before either claim existed.
    homeRegion: c.region ?? c.cluster ?? '',
    roles: c.roles ?? [],
  };
}

const TOKEN_STORAGE_KEY = 'vex.console.token';

function persist(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // localStorage may be disabled (private mode / SSR) — ignore.
  }
}

function recall(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export const httpAuthAdapter: AuthAdapter = {
  async signIn(username, password) {
    const { authUrl } = getConsoleConfig();
    const res = await fetch(`${authUrl}/auth/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: username,
        client_secret: password,
      }),
    });
    if (!res.ok) throw new Error(`Sign-in failed: HTTP ${res.status}`);
    const out = (await res.json()) as { access_token: string; expires_in: number };
    persist(out.access_token);
    return { user: userFromClaims(out.access_token, username), token: out.access_token };
  },
  async signOut() {
    persist(null);
  },
  async whoami() {
    const token = recall();
    if (!token) return null;
    const claims = decodeJwtClaims(token);
    if (!claims) {
      persist(null);
      return null;
    }
    // Expired tokens are treated as no-session — the AuthProvider will
    // render the LoginScreen rather than propagate a stale JWT into every
    // downstream API call (which would 401 with a cryptic message).
    if (isJwtExpired(token)) {
      persist(null);
      return null;
    }
    return { user: userFromClaims(token), token };
  },
  async signUp(payload: SignUpPayload) {
    const { authUrl } = getConsoleConfig();
    const res = await fetch(`${authUrl}/auth/signup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // Surface the server's JSON error shape when available so the
      // SignUpScreen can highlight the offending field.
      let msg: string | undefined;
      try {
        const j = (await res.json()) as { error?: string; message?: string };
        msg = j.message ?? j.error;
      } catch {
        /* non-JSON — fall through to generic message */
      }
      const err = new Error(msg ?? `Sign-up failed: HTTP ${res.status}`);
      // Tag so callers can distinguish 4xx (bad input) from 5xx (retryable).
      (err as { status?: number }).status = res.status;
      throw err;
    }
    const out = (await res.json()) as { accessToken: string; username: string; tenant: string };
    persist(out.accessToken);
    return { user: userFromClaims(out.accessToken, out.username), token: out.accessToken };
  },
};

/**
 * Complete an OIDC Authorization Code exchange. Called by the /sso/callback
 * route after vex-id redirects back with ?code=…&state=… . Uses the same
 * console-side proxy paths (/token) that the LoginScreen SSO button targets,
 * so no runtime-config change is needed to switch clusters.
 */
export async function exchangeAuthorizationCode(
  code: string,
  redirectUri: string,
  clientId = 'vextura-console',
): Promise<{ token: string; user: AuthUser }> {
  // OIDC token endpoint accepts application/x-www-form-urlencoded per RFC 6749.
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
  });
  const res = await fetch('/token', {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`SSO code exchange failed: HTTP ${res.status}`);
  }
  const out = (await res.json()) as { access_token: string };
  if (!out.access_token) {
    throw new Error('SSO code exchange returned no access_token');
  }
  persist(out.access_token);
  return { token: out.access_token, user: userFromClaims(out.access_token) };
}

/**
 * Build the OIDC authorize URL the LoginScreen SSO button navigates to.
 * Uses the console-origin proxied /authorize path (nginx forwards to vex-gate).
 */
export function ssoAuthorizeUrl(clientId = 'vextura-console'): string {
  const state = cryptoRandomState();
  try {
    sessionStorage.setItem('vex.console.sso.state', state);
  } catch {
    /* ignore */
  }
  const redirectUri = `${window.location.origin}/sso/callback`;
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid profile email',
    redirect_uri: redirectUri,
    state,
  });
  return `/authorize?${params.toString()}`;
}

/** Retrieve + clear the anti-CSRF state we stashed before starting SSO. */
export function readAndClearSsoState(): string | null {
  try {
    const s = sessionStorage.getItem('vex.console.sso.state');
    sessionStorage.removeItem('vex.console.sso.state');
    return s;
  } catch {
    return null;
  }
}

function cryptoRandomState(): string {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Loads the dev mock user from public/dev-user.json. Used ONLY when
 * VITE_VEX_MOCK_AUTH=1 at build time. The fixture file is dev-only and
 * customer images should set VITE_VEX_MOCK_AUTH=0.
 */
export async function loadDevUser(): Promise<AuthUser> {
  const res = await fetch('/dev-user.json');
  if (!res.ok) {
    throw new Error(
      'VITE_VEX_MOCK_AUTH=1 but /dev-user.json was not served. Either set VITE_VEX_MOCK_AUTH=0 (use http auth) or restore the fixture.',
    );
  }
  return (await res.json()) as AuthUser;
}

/**
 * Dev-mock adapter that mints a REAL JWT via vex-auth's /auth/token endpoint
 * on every signIn/whoami — so downstream SDK calls carry a token the gate
 * actually accepts. Uses seeded admin credentials from /dev-user.json.
 *
 * The pre-existing createMockAuthAdapter (in @vextura/shell) returns a
 * literal `"mock-jwt-token"` string — great for shell-only unit tests, but
 * every SDK call against a real gate then 401s with "malformed
 * Authorization header." This adapter closes that gap for local dev
 * without forcing operators to flip VITE_VEX_MOCK_AUTH off.
 *
 * Fallback: if /auth/token is unreachable (vex-auth container down), we
 * degrade to the mock-jwt-token behaviour + log a WARN. That keeps
 * shell-only smoke tests functional even when the backend stack is off.
 */
export function createDevBackedAuthAdapter(
  user: AuthUser,
  clientId: string,
  clientSecret: string,
) {
  return {
    async signIn() {
      const token = await fetchDevToken(clientId, clientSecret);
      return { user, token };
    },
    async signOut() {
      persist(null);
    },
    async whoami() {
      const cached = recall();
      if (cached && !isJwtExpired(cached)) {
        return { user, token: cached };
      }
      const token = await fetchDevToken(clientId, clientSecret);
      return { user, token };
    },
  };
}

async function fetchDevToken(clientId: string, clientSecret: string): Promise<string> {
  const { authUrl } = getConsoleConfig();
  try {
    const res = await fetch(`${authUrl}/auth/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
      }),
    });
    if (!res.ok) throw new Error(`vex-auth /auth/token → ${res.status}`);
    const out = (await res.json()) as { access_token: string };
    persist(out.access_token);
    return out.access_token;
  } catch (err) {
    console.warn('[dev-auth] vex-auth unreachable — falling back to inert mock token', err);
    return 'mock-jwt-token';
  }
}
